/**
 * Casework — Phase 3's instancing workhorse: parametric boxes, heavily instanced per
 * TYPE. Style variants are conditional TYPE params + generator branches (Addendum 2 —
 * no family editor): base (counter w/ overhang), upper (lifted to its mount height —
 * `mountHeightFt` is conditionalOn the upper style), tall.
 *
 * FREESTANDING round (2026-08-19): converted from wall-hosted (click a wall, position
 * = hostId + hostOffsetFt) to freestanding placement — the OBJECTS round's own model,
 * exactly (no host, no auto-coupling; the designer places a cabinet where it goes, same
 * as an appliance or a piece of furniture). Position = a single-node `path` param (the
 * anchor at the box's BACK-CENTER, the objectKinds.ts frame convention) + `rotationDeg`
 * + `heightOffsetFt` — see the instance Placement group below. `sideFlip` (which wall
 * face the box hugged) is gone: a freestanding box picks its own facing via rotation,
 * so the flag had nothing left to mean. Existing wall-hosted casework is migrated once,
 * non-destructively, on doc open (`migrateCaseworkToFreestanding`,
 * commands/elements.ts) — the computed anchor/rotation reproduce the exact same world
 * position/facing the element already had, so nothing visually moves.
 *
 * TYPE/INSTANCE GOVERNANCE round (2026-08-09): WIDTH and (optionally) HEIGHT moved to
 * instance params — see the comments on those fields below. Since the casework
 * prototype MESH itself is shaped by width/height (front-layout tiling, mount height),
 * `protoKeyFor` (bim-geometry/categories/opening/protos.ts) forks the instancing cache
 * on the element's EFFECTIVE width/height in addition to the type params — 50 identical
 * cabinets (same type, same width, same effective height) still share ONE prototype +
 * 50 transforms (the Phase-3 acceptance survives unchanged), but two instances of the
 * same type at different widths now correctly get their own prototypes.
 */
import type { CategorySchema } from '../../registry'

export const caseworkSchema: CategorySchema = {
  id: 'casework',
  // casework sits on the floor and against walls — it wins overlapping 3D picks like
  // openings/objects do (plan pick priority lives in the app's pickConfigs entry)
  pickRank: 0,
  label: 'Casework',
  typeParams: {
    groups: [
      {
        label: 'Box',
        params: [
          {
            key: 'style', label: 'Style', kind: 'choice', default: 'base',
            choices: [
              { value: 'base', label: 'Base (w/ counter)' },
              { value: 'upper', label: 'Upper' },
              { value: 'tall', label: 'Tall' },
              // CLOSET ROD round (2026-10-01, Matt: "a closet pole and shelf … show
              // garments in plan view like you would see in a typical architectural
              // plan … draw it from end to end and change the length in plan view with
              // handles"): no box — a shelf over a hanging rod. Depth = the HANGING
              // zone (the rod sits at its middle); Height = the TOP of the shelf. Drawn
              // with the Closet Rod tool (the Casework Run gesture), so the run's end
              // grips set its length.
              { value: 'closet', label: 'Closet Rod & Shelf' },
            ],
          },
          // TYPE/INSTANCE GOVERNANCE round (2026-08-09, Matt): WIDTH moved to an
          // INSTANCE param below — "a home will have many many widths for the same
          // cabinet type" made a type-per-width unsustainable. DEPTH stays TYPE (a
          // product-line constant; a 15"-deep base is a different product). HEIGHT
          // stays TYPE too — it's the DEFAULT a new instance inherits (see the
          // nullable `heightFt` instance override below); uppers legitimately vary
          // per condition (soffits, over-fridge, over-range) so a per-instance
          // override exists, but most cabinets just take the type's height as-is.
          // BOX DEPTH (2026-10-21, Matt): DEPTH is now only the DEFAULT a newly drawn box takes —
          // every box can set its own (`boxStyle.depthFt`, the instance `boxDepthFt` field below,
          // and the Casework Studio's Size section), so a type no longer fixes a run's depth.
          { key: 'depthFt', label: 'Default Depth', kind: 'length', default: 2, min: 0.5, max: 4 },
          { key: 'heightFt', label: 'Height', kind: 'length', default: 3, min: 1, max: 9 },
          {
            key: 'rodHeightFt', label: 'Rod Height', kind: 'length', default: 5.5, min: 2, max: 8,
            conditionalOn: { param: 'style', equals: 'closet' },
          },
          {
            key: 'shelfDepthFt', label: 'Shelf Depth', kind: 'length', default: 14 / 12, min: 0.5, max: 2,
            conditionalOn: { param: 'style', equals: 'closet' },
          },
          {
            key: 'mountHeightFt', label: 'Mount Height', kind: 'length', default: 4.5, min: 2, max: 8,
            conditionalOn: { param: 'style', equals: 'upper' },
          },
          {
            key: 'toeKick', label: 'Toe Kick', kind: 'bool', default: true,
            conditionalOn: { param: 'style', equals: ['base', 'tall'] },
          },
          {
            // CASEWORK STUDIO (2026-10-09): the base, as a choice — a recessed toe kick, flush to
            // the floor, or a FURNITURE BASE (an applied base board, proud of the fronts, wrapping
            // the run's exposed ends). '' = follow Toe Kick above (the older on/off)
            key: 'baseStyle', label: 'Base', kind: 'choice', default: '',
            choices: [
              { value: '', label: 'From Toe Kick' },
              { value: 'toe', label: 'Recessed toe kick' },
              { value: 'flush', label: 'Flush to the floor' },
              { value: 'furniture', label: 'Furniture base' },
              { value: 'feet', label: 'Feet' },
              // 2026-10-09 (Matt): a wall-hung cabinet clear of the floor; and with a face
              // frame, its stiles running on down to the floor as legs over a recessed toe
              { value: 'floating', label: 'Floating' },
              { value: 'legs', label: 'Frame legs' },
            ],
            conditionalOn: { param: 'style', equals: ['base', 'tall'] },
          },
          {
            // FLOATING (2026-10-09): how far the case hangs clear of the floor (inches)
            key: 'floatHeightIn', label: 'Clear of Floor (in)', kind: 'number', default: 10, min: 2, max: 30,
            conditionalOn: { param: 'baseStyle', equals: ['floating'] },
          },
          // CASEWORK STUDIO stage 2 (Matt: "different profiles for the base as options")
          {
            key: 'baseProfile', label: 'Base Profile', kind: 'choice', default: 'square',
            choices: [
              { value: 'square', label: 'Square' }, { value: 'eased', label: 'Eased' }, { value: 'bevel', label: 'Bevel' },
              { value: 'cove', label: 'Cove' }, { value: 'ogee', label: 'Ogee' }, { value: 'plinth', label: 'Plinth' },
            ],
            conditionalOn: [{ param: 'style', equals: ['base', 'tall'] }, { param: 'baseStyle', equals: 'furniture' }],
          },
          {
            key: 'footStyle', label: 'Feet', kind: 'choice', default: 'bun',
            choices: [{ value: 'bun', label: 'Bun' }, { value: 'bracket', label: 'Bracket' }, { value: 'tapered', label: 'Tapered Leg' }],
            conditionalOn: [{ param: 'style', equals: ['base', 'tall'] }, { param: 'baseStyle', equals: 'feet' }],
          },
          {
            key: 'baseHeightIn', label: 'Base Height (in)', kind: 'number', default: 4, min: 2, max: 8,
            conditionalOn: { param: 'style', equals: ['base', 'tall'] },
          },
          {
            key: 'crownStyle', label: 'Crown', kind: 'choice', default: 'none',
            choices: [
              { value: 'none', label: 'None' }, { value: 'flat', label: 'Flat Frieze' }, { value: 'cove', label: 'Cove Crown' },
              { value: 'ogee', label: 'Ogee Crown' }, { value: 'stepped', label: 'Stepped Crown' },
            ],
            conditionalOn: { param: 'style', equals: ['upper', 'tall'] },
          },
          {
            key: 'crownHeightIn', label: 'Crown Height (in)', kind: 'number', default: 3.5, min: 1.5, max: 8,
            conditionalOn: { param: 'style', equals: ['upper', 'tall'] },
          },
          {
            key: 'lightRail', label: 'Light Rail', kind: 'choice', default: 'none',
            choices: [{ value: 'none', label: 'None' }, { value: 'square', label: 'Square' }, { value: 'cove', label: 'Cove' }, { value: 'bead', label: 'Bead' }],
            conditionalOn: { param: 'style', equals: 'upper' },
          },
        ],
      },
      {
        // r11 (Matt's kitchen test case): common cabinet configurations + style
        // variants as TYPE params — protoKey forks a prototype per combination and
        // featureEdges derives the elevation linework from whatever gets built, so
        // every variant is data + a generator branch, never a family edit.
        label: 'Fronts',
        params: [
          {
            key: 'frontConfig', label: 'Configuration', kind: 'choice', default: 'doors',
            choices: [
              { value: 'doors', label: 'Doors' },
              { value: 'drawers', label: 'Drawer Stack' },
              { value: 'door-drawer', label: 'Drawer over Doors' },
              { value: 'sink', label: 'Sink Base (False Front + Doors)' },
              { value: 'open', label: 'Open Shelves' },
              { value: 'trash', label: 'Trash Drawer' },
              // CASEWORK ROWS stage 4 (2026-10-21): an empty spot under the counter for a desk or a
              // makeup counter; the counter runs on over it
              { value: 'knee', label: 'Knee Space' },
              // APPLIANCE INTEGRATION (2026-10-23): a gap in the casework for a freestanding range or
              // refrigerator — no cabinet and no counter; the neighbours' sides are its sides
              { value: 'slot', label: 'Appliance Slot (no cabinet)' },
            ],
            conditionalOn: { param: 'style', equals: ['base', 'upper', 'tall'] },
          },
          {
            // CASEWORK STUDIO (2026-10-09): over a door PAIR, the drawer (or sink false front) band
            // as one long piece, or split to match the doors
            key: 'drawerBand', label: 'Drawer over a Pair', kind: 'choice', default: 'match',
            choices: [{ value: 'match', label: 'Match the Doors' }, { value: 'one', label: 'One Long Drawer' }],
            conditionalOn: [{ param: 'style', equals: ['base', 'upper', 'tall'] }, { param: 'frontConfig', equals: ['door-drawer', 'sink'] }],
          },
          {
            // what runs across the top of a knee space (Matt, Q5: a drawer by default)
            key: 'kneeApron', label: 'Knee Space Apron', kind: 'choice', default: 'drawer',
            choices: [{ value: 'drawer', label: 'Drawer' }, { value: 'false', label: 'False Front' }, { value: 'none', label: 'None' }],
            conditionalOn: [{ param: 'style', equals: ['base'] }, { param: 'frontConfig', equals: 'knee' }],
          },
          {
            key: 'drawerCount', label: 'Drawer Count', kind: 'number', default: 3, min: 2, max: 6,
            conditionalOn: [{ param: 'style', equals: ['base', 'upper', 'tall'] }, { param: 'frontConfig', equals: 'drawers' }],
          },
          {
            // 2026-10-21 (Matt): a drawer stack as a 6" top drawer with the rest equal under it,
            // or every drawer the same height
            key: 'drawerSizing', label: 'Drawer Heights', kind: 'choice', default: 'top',
            choices: [{ value: 'top', label: '6" Top, Rest Equal' }, { value: 'equal', label: 'All Equal' }],
            conditionalOn: [{ param: 'style', equals: ['base', 'upper', 'tall'] }, { param: 'frontConfig', equals: 'drawers' }],
          },
          {
            // r12: single doors follow this; pairs always hinge outboard. Drives the
            // dashed opening-line V in elevations (point = hinge stile).
            key: 'hingeSide', label: 'Hinge Side (single door)', kind: 'choice', default: 'left',
            choices: [
              { value: 'left', label: 'Left' },
              { value: 'right', label: 'Right' },
            ],
            conditionalOn: [{ param: 'style', equals: ['base', 'upper', 'tall'] }, { param: 'frontConfig', equals: ['doors', 'door-drawer', 'sink'] }],
          },
          {
            key: 'frameMode', label: 'Frame Style', kind: 'choice', default: 'frameless',
            choices: [
              { value: 'frameless', label: 'Frameless (Full Overlay)' },
              { value: 'overlay', label: 'Face Frame — Overlay' },
              { value: 'inset', label: 'Face Frame — Inset' },
              // CASEWORK STUDIO stage 2: inset with a bead run around every frame opening
              { value: 'beaded', label: 'Face Frame — Beaded Inset' },
            ],
            conditionalOn: [{ param: 'style', equals: ['base', 'upper', 'tall'] }, { param: 'frontConfig', equals: ['doors', 'drawers', 'door-drawer', 'sink'] }],
          },
          {
            key: 'panelStyle', label: 'Door/Drawer Panel', kind: 'choice', default: 'slab',
            choices: [
              { value: 'slab', label: 'Flat (Slab)' },
              { value: 'shaker', label: 'Shaker' },
              // CASEWORK STUDIO stage 2 (caseworkCatalog.ts#DOOR_PROFILES)
              { value: 'slimShaker', label: 'Slim Shaker' },
              { value: 'beadedShaker', label: 'Beaded Shaker' },
              { value: 'raised', label: 'Raised Panel' },
              { value: 'applied', label: 'Applied Moulding' },
              { value: 'beadboard', label: 'Beadboard' },
              { value: 'fluted', label: 'Fluted' },
              { value: 'glass', label: 'Glass' },
              { value: 'glassLites', label: 'Divided-Lite Glass' },
            ],
            conditionalOn: [{ param: 'style', equals: ['base', 'upper', 'tall'] }, { param: 'frontConfig', equals: ['doors', 'drawers', 'door-drawer', 'sink'] }],
          },
        ],
      },
      {
        // CASEWORK STUDIO stage 4 (2026-10-09): the countertop and backsplash — caseworkCatalog.ts.
        // Base boxes only; run style + box overrides like everything the Studio sets.
        label: 'Countertop',
        params: [
          {
            key: 'counterThicknessIn', label: 'Thickness (in)', kind: 'number', default: 1.5, min: 0.5, max: 4,
            conditionalOn: { param: 'style', equals: ['base'] },
          },
          {
            key: 'counterEdge', label: 'Edge', kind: 'choice', default: 'eased',
            choices: [
              { value: 'eased', label: 'Eased' }, { value: 'bevel', label: 'Beveled' },
              { value: 'halfBullnose', label: 'Half Bullnose' }, { value: 'fullBullnose', label: 'Full Bullnose' },
              { value: 'ogee', label: 'Ogee' }, { value: 'mitered', label: 'Mitered Apron' },
            ],
            conditionalOn: { param: 'style', equals: ['base'] },
          },
          {
            key: 'counterFrontOverIn', label: 'Front Overhang (in)', kind: 'number', default: 1, min: 0, max: 18,
            conditionalOn: { param: 'style', equals: ['base'] },
          },
          {
            // the overhang at an END of the top (where no neighbouring counter continues it)
            key: 'counterEndOverIn', label: 'End Overhang (in)', kind: 'number', default: 1, min: 0, max: 18,
            conditionalOn: { param: 'style', equals: ['base'] },
          },
          {
            // named as seen standing in the room facing the fronts
            key: 'counterWaterfall', label: 'Waterfall', kind: 'choice', default: 'none',
            choices: [
              { value: 'none', label: 'None' }, { value: 'left', label: 'Left End' },
              { value: 'right', label: 'Right End' }, { value: 'both', label: 'Both Ends' },
            ],
            conditionalOn: { param: 'style', equals: ['base'] },
          },
          {
            key: 'counterSplash', label: 'Backsplash', kind: 'choice', default: 'none',
            choices: [
              { value: 'none', label: 'None' }, { value: '4', label: '4"' },
              { value: '6', label: '6"' }, { value: 'full', label: 'Full Height' },
            ],
            conditionalOn: { param: 'style', equals: ['base'] },
          },
          {
            // '' = the countertop's own material
            key: 'splashMaterial', label: 'Backsplash Material', kind: 'material', default: '',
            conditionalOn: { param: 'style', equals: ['base'] },
          },
        ],
      },
      {
        // CASEWORK STUDIO stage 3 (2026-10-09): interiors, power, lighting — caseworkCatalog.ts
        label: 'Interior, Power & Lighting',
        params: [
          {
            key: 'interiorFit', label: 'Interior', kind: 'choice', default: 'shelves',
            choices: [
              { value: 'shelves', label: 'Shelves' }, { value: 'rollouts', label: 'Roll-out Trays' },
              { value: 'trash', label: 'Trash Pull-out' }, { value: 'spice', label: 'Spice Pull-out' },
              { value: 'trays', label: 'Tray Dividers' }, { value: 'plateRack', label: 'Plate Rack' },
              { value: 'pantry', label: 'Pantry Roll-outs' }, { value: 'garage', label: 'Appliance Garage' },
            ],
            conditionalOn: { param: 'style', equals: ['base', 'upper', 'tall'] },
          },
          { key: 'shelfCount', label: 'Shelves (0 = auto, -1 = none)', kind: 'number', default: 0, min: -1, max: 8 },
          { key: 'glassShelves', label: 'Glass Shelves', kind: 'bool', default: false },
          {
            // CASEWORK ROWS stage 3 (2026-10-21): what you see behind open shelves
            key: 'backPanel', label: 'Back Panel', kind: 'choice', default: 'plain',
            choices: [{ value: 'plain', label: 'Plain' }, { value: 'beadboard', label: 'Beadboard' }, { value: 'none', label: 'None (the wall shows)' }],
            conditionalOn: { param: 'frontConfig', equals: 'open' },
          },
          {
            key: 'drawerInsert', label: 'Drawer Insert', kind: 'choice', default: 'none',
            choices: [
              { value: 'none', label: 'None' }, { value: 'cutlery', label: 'Cutlery' }, { value: 'knife', label: 'Knife Block' },
              { value: 'utensil', label: 'Utensils' }, { value: 'spice', label: 'Spice Tiers' }, { value: 'pegs', label: 'Peg Organizer' },
            ],
          },
          { key: 'pwrDrawer', label: 'Charging Drawer Outlet', kind: 'bool', default: false },
          { key: 'pwrPopup', label: 'Pop-up Counter Outlet', kind: 'bool', default: false },
          { key: 'pwrInterior', label: 'Interior Outlet', kind: 'bool', default: false },
          { key: 'plugMold', label: 'Under-cabinet Plug Mold', kind: 'bool', default: false },
          { key: 'ledUnder', label: 'Under-cabinet LED Tape', kind: 'bool', default: false },
          { key: 'puckUnder', label: 'Under-cabinet Puck Lights', kind: 'bool', default: false },
          { key: 'ledInterior', label: 'Interior LED', kind: 'bool', default: false },
          { key: 'ledToe', label: 'Toe-kick LED', kind: 'bool', default: false },
          { key: 'ledAbove', label: 'Above-cabinet Uplight', kind: 'bool', default: false },
          { key: 'lightTempK', label: 'Light Color (K)', kind: 'number', default: 3000, min: 2200, max: 5000 },
        ],
      },
      {
        label: 'Appearance',
        params: [
          // P6a: '' = the built-in joinery look (pre-P6a behavior)
          { key: 'surfaceMaterial', label: 'Material', kind: 'material', default: '' },
          // ATMOSPHERE VIEW Stage 3 (docs/bim-plan/21 §4): per-part finishes. '' =
          // follow `surfaceMaterial` above, so every existing type renders exactly as
          // before; hardware '' = a built-in brushed metal.
          { key: 'frontMaterial', label: 'Fronts', kind: 'material', default: '' },
          { key: 'carcassMaterial', label: 'Box', kind: 'material', default: '' },
          { key: 'counterMaterial', label: 'Countertop', kind: 'material', default: '' },
          {
            // CASEWORK STUDIO (2026-10-09, Matt: "decide the hardware on the drawers vs. the doors
            // independently"): '' = the Pulls choice below
            key: 'doorPullStyle', label: 'Door Hardware', kind: 'choice', default: '',
            choices: [
              { value: '', label: 'Same as Pulls' },
              { value: 'none', label: 'None' },
              { value: 'knob', label: 'Round knob' },
              { value: 'knobMushroom', label: 'Mushroom knob' },
              { value: 'knobSquare', label: 'Square knob' },
              { value: 'bar', label: 'Bar pull' },
              { value: 'barRound', label: 'Round bar' },
              { value: 'cup', label: 'Cup pull' },
              { value: 'edge', label: 'Edge pull' },
              // FINGER GROOVE (2026-10-23, team ask): a grip routed into the front — no hardware
              { value: 'groove', label: 'Routed finger groove' },
              { value: 'ring', label: 'Ring pull' },
              { value: 'latch', label: 'Cabinet latch' },
              { value: 'appliance', label: 'Appliance pull' },
            ],
            conditionalOn: { param: 'style', equals: ['base', 'upper', 'tall'] },
          },
          {
            key: 'drawerPullStyle', label: 'Drawer Hardware', kind: 'choice', default: '',
            choices: [
              { value: '', label: 'Same as Pulls' },
              { value: 'none', label: 'None' },
              { value: 'knob', label: 'Round knob' },
              { value: 'knobMushroom', label: 'Mushroom knob' },
              { value: 'knobSquare', label: 'Square knob' },
              { value: 'bar', label: 'Bar pull' },
              { value: 'barRound', label: 'Round bar' },
              { value: 'cup', label: 'Cup pull' },
              { value: 'edge', label: 'Edge pull' },
              // FINGER GROOVE (2026-10-23, team ask): a grip routed into the front — no hardware
              { value: 'groove', label: 'Routed finger groove' },
              { value: 'ring', label: 'Ring pull' },
              { value: 'latch', label: 'Cabinet latch' },
              { value: 'appliance', label: 'Appliance pull' },
            ],
            conditionalOn: { param: 'style', equals: ['base', 'upper', 'tall'] },
          },
          {
            // '' = the drawer fronts follow the door profile (glass doors → shaker drawers)
            key: 'drawerPanelStyle', label: 'Drawer Profile', kind: 'choice', default: '',
            choices: [
              { value: '', label: 'Match the Doors' }, { value: 'slab', label: 'Flat (Slab)' }, { value: 'shaker', label: 'Shaker' },
              { value: 'slimShaker', label: 'Slim Shaker' }, { value: 'beadedShaker', label: 'Beaded Shaker' },
              { value: 'raised', label: 'Raised Panel' }, { value: 'applied', label: 'Applied Moulding' },
              { value: 'beadboard', label: 'Beadboard' }, { value: 'fluted', label: 'Fluted' },
            ],
            conditionalOn: { param: 'style', equals: ['base', 'upper', 'tall'] },
          },
          { key: 'doorPullSizeIn', label: 'Door Pull Length (in)', kind: 'number', default: 0, min: 0, max: 24 },
          { key: 'drawerPullSizeIn', label: 'Drawer Pull Length (in)', kind: 'number', default: 0, min: 0, max: 24 },
          { key: 'twoPullsWide', label: 'Two Pulls on Wide Drawers', kind: 'bool', default: true },
          {
            key: 'hardwareFinish', label: 'Hardware Finish', kind: 'choice', default: '',
            choices: [
              { value: '', label: 'Brushed (default)' }, { value: 'nickel', label: 'Polished Nickel' },
              { value: 'stainless', label: 'Satin Stainless' }, { value: 'brass', label: 'Polished Brass' },
              { value: 'agedBrass', label: 'Unlacquered Brass' }, { value: 'bronze', label: 'Oil-Rubbed Bronze' },
              { value: 'copper', label: 'Copper' }, { value: 'blackened', label: 'Matte Black' },
            ],
          },
          {
            key: 'pullStyle', label: 'Pulls', kind: 'choice', default: 'none',
            choices: [
              { value: 'none', label: 'None' },
              { value: 'bar', label: 'Bar pulls' },
              { value: 'knob', label: 'Knobs' },
              { value: 'groove', label: 'Routed finger grooves' },
            ],
            conditionalOn: { param: 'style', equals: ['base', 'upper', 'tall'] },
          },
          { key: 'hardwareMaterial', label: 'Hardware', kind: 'material', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        // COLORS (2026-10-06): the Paint Brush's color on the whole cabinet (colors.ts id) — over its surface finish, 3D only
        label: 'Color',
        params: [
          { key: 'paintColor', label: 'Color', kind: 'data', default: null, hidden: true },
        ],
      },
      {
        label: 'Box',
        params: [
          // WIDTH is purely instance (see the typeParams comment above) — no type
          // default to shadow, just an ordinary editable size.
          { key: 'widthFt', label: 'Width', kind: 'length', default: 2, min: 0.75, max: 8 },
          // HEIGHT: nullable override shadowing the TYPE's `heightFt` key in resolved
          // params (the ROOF round's overhangFt/slopeRise pattern) — null/absent =
          // "inherit the type's height." Consumers must read
          // `el.params.heightFt ?? el.typeParams.heightFt`, never `el.params.heightFt`
          // alone (see caseworkSolid/buildCaseworkProto in bim-geometry).
          { key: 'heightFt', label: 'Height', kind: 'length', default: null, optional: true, min: 1, max: 9 },
          // BOX DEPTH (2026-10-21): THIS box's depth, typed. Never stored under this key —
          // elements.ts#applyParamEdit writes `boxStyle.depthFt` (the Studio's override, one
          // source) and foldCaseworkStyle reads it back here. Blank = the type's default depth.
          {
            key: 'boxDepthFt', label: 'Depth', kind: 'length', default: null, optional: true, min: 0.5, max: 4,
            optionalHint: 'type default depth',
          },
          // UPPER HEIGHT (2026-10-15, Armando: "Is there a way to edit the placement height of an
          // upper cabinet? … over the fridge"): THIS upper's bottom above the floor, typed. Never
          // stored under this key — elements.ts#applyParamEdit writes the box's own
          // `boxStyle.mountHeightFt` (the Studio's override, one source), and foldCaseworkStyle
          // reads the effective value back here for the palette. Blank = the type's mount height.
          {
            key: 'upperBottomFt', label: 'Bottom at (from floor)', kind: 'length', default: null, optional: true, min: 1, max: 9,
            optionalHint: 'type mount height',
            conditionalOn: { param: 'style', equals: 'upper' },
          },
        ],
      },

      {
        label: 'Placement',
        params: [
          // single-node anchor path — see the header note; kind 'path2d' is skipped by
          // eyedropper transfer and rendered read-only by the palette (objectSchema's
          // own pattern)
          { key: 'path', label: 'Anchor', kind: 'path2d', default: null },
          {
            // 0 faces world +y; the rotate grip and placement aim write this
            key: 'rotationDeg', label: 'Rotation', kind: 'number', default: 0,
            min: -360, max: 360, positional: true,
          },
          // vertical position is unaffected by this round — upper cabinets still hang
          // from the TYPE's mountHeightFt, base/tall still sit at 0; that was already
          // host-independent (no floor/level equivalent of "which wall"), so it needed
          // no change when the host went away.
          // countertop auto-join (2026-07-28 decision): counters live IN the base
          // cabinet and adjacent units' slabs DERIVE one continuous top — this
          // per-UNIT switch is the design-freedom escape hatch (suppress the slab
          // where a standalone/custom counter will cover the run). Base style only;
          // upper/tall generate no counter regardless.
          { key: 'hasCounter', label: 'Countertop (base)', kind: 'bool', default: true },
        ],
      },
      {
        // CASEWORK RUNS (2026-10-01, commands/caseworkRun.ts): run membership. All
        // kind 'data' — never a form field, never carried by the eyedropper. Declared
        // here (not left schema-free) so resolveElement passes them through.
        label: 'Run',
        params: [
          { key: 'runId', label: 'Run', kind: 'data', default: null, hidden: true },
          { key: 'runLengthFt', label: 'Run Length', kind: 'data', default: null, hidden: true },
          { key: 'runSized', label: 'Width Set', kind: 'data', default: null, hidden: true },
          // CASEWORK AS ONE ITEM (2026-10-09, Matt): the length was typed in the Casework Studio —
          // the plan's end handles then refuse and send you to the Studio (shared by every box)
          { key: 'runLengthSet', label: 'Length Set in Studio', kind: 'data', default: null, hidden: true },
          { key: 'runMass', label: 'Massing', kind: 'data', default: null, hidden: true },
          // CASEWORK STUDIO (2026-10-08): the run's STYLE (shared by every member, like
          // runLengthFt) and this box's OVERRIDES of it — both folded over the type's params
          // at resolve time (caseworkStyle.ts#foldCaseworkStyle), so every consumer reads
          // the effective value off typeParams with no change of its own
          { key: 'runStyle', label: 'Run Style', kind: 'data', default: null, hidden: true },
          { key: 'boxStyle', label: 'Box Overrides', kind: 'data', default: null, hidden: true },
          // CASEWORK STUDIO divisions: 'filler' | 'panel' = this member is a filler strip or a
          // finished panel standing at a division (or an end) of the run, not a cabinet
          { key: 'runPart', label: 'Division Part', kind: 'data', default: null, hidden: true },
        ],
      },
    ],
  },
}
